import { useCallback, useEffect, useRef, useState } from "react";
import {
  KEEP_ALIVE_MS,
  SPEAK_GAP_MS,
  cancelSpeech,
  createUtterance,
  isGhostSpeechEnd,
  isIgnorableSpeechError,
  isLikelyIOS,
  isSpeechSupported,
  langPrefix,
  loadVoices,
  pauseSpeech,
  resumeSpeech,
  speakNow,
} from "./speech";
import { loadSpeechPrefs, saveSpeechPrefs } from "./speechPrefs";

const initialPrefs = loadSpeechPrefs();

const initialState = {
  supported: true,
  status: "idle",
  index: -1,
  total: 0,
  current: null,
  voices: [],
  rate: initialPrefs.rate,
  autoAdvance: initialPrefs.autoAdvance,
  voiceURI: initialPrefs.voiceURI,
  sleepTimer: null,
};

const useSpeech = ({ onComplete } = {}) => {
  const [state, setState] = useState({
    ...initialState,
    supported: isSpeechSupported(),
  });
  const itemsRef = useRef([]);
  const indexRef = useRef(-1);
  const utteranceRef = useRef(null);
  const voicesRef = useRef([]);
  const statusRef = useRef("idle");
  const sessionRef = useRef(0);
  const keepAliveRef = useRef(null);
  const sleepTimerIntervalRef = useRef(null);
  const sleepTimerRef = useRef(null);
  const optionsRef = useRef({
    rate: initialPrefs.rate,
    voiceURI: initialPrefs.voiceURI,
    title: "",
  });
  const onCompleteRef = useRef(onComplete);
  const pauseRef = useRef(() => {});
  const resumeRef = useRef(() => {});
  const stopRef = useRef(() => {});
  const skipRef = useRef(() => {});
  const speakAtRef = useRef(() => {});
  onCompleteRef.current = onComplete;

  const setStatus = useCallback((status, extra = {}) => {
    statusRef.current = status;
    setState((prev) => ({
      ...prev,
      status,
      index: indexRef.current,
      total: itemsRef.current.length,
      current: itemsRef.current[indexRef.current] || null,
      ...extra,
    }));
  }, []);

  const clearKeepAlive = () => {
    if (keepAliveRef.current) {
      window.clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  };

  const bumpSession = () => {
    sessionRef.current += 1;
    return sessionRef.current;
  };

  const syncMediaSession = useCallback((status) => {
    if (typeof navigator === "undefined" || !navigator.mediaSession) return;
    try {
      navigator.mediaSession.playbackState =
        status === "playing" ? "playing" : status === "paused" ? "paused" : "none";
    } catch (error) {
      // ignore unsupported states
    }
  }, []);

  const stopInternal = useCallback(
    ({ completed = false } = {}) => {
      bumpSession();
      clearKeepAlive();
      utteranceRef.current = null;
      indexRef.current = -1;
      itemsRef.current = [];
      cancelSpeech();
      setStatus("idle");
      syncMediaSession("idle");

      const isChapterTimer = sleepTimerRef.current?.type === "chapter";
      if (isChapterTimer) {
        sleepTimerRef.current = null;
        setState((prev) => ({ ...prev, sleepTimer: null }));
        return;
      }

      if (completed) {
        onCompleteRef.current?.();
      }
    },
    [setStatus, syncMediaSession]
  );

  const setSleepTimer = useCallback(
    (value) => {
      if (sleepTimerIntervalRef.current) {
        window.clearInterval(sleepTimerIntervalRef.current);
        sleepTimerIntervalRef.current = null;
      }

      if (!value) {
        sleepTimerRef.current = null;
        setState((prev) => ({ ...prev, sleepTimer: null }));
        return;
      }

      if (value === "chapter") {
        const timerData = { type: "chapter" };
        sleepTimerRef.current = timerData;
        setState((prev) => ({ ...prev, sleepTimer: timerData }));
        return;
      }

      const minutes = Number(value);
      if (!Number.isFinite(minutes) || minutes <= 0) {
        sleepTimerRef.current = null;
        setState((prev) => ({ ...prev, sleepTimer: null }));
        return;
      }

      const endsAt = Date.now() + minutes * 60 * 1000;
      const initialRemaining = minutes * 60;
      const timerData = {
        type: "minutes",
        minutes,
        endsAt,
        remainingSeconds: initialRemaining,
      };
      sleepTimerRef.current = timerData;
      setState((prev) => ({ ...prev, sleepTimer: timerData }));

      sleepTimerIntervalRef.current = window.setInterval(() => {
        const remaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
        if (remaining <= 0) {
          if (sleepTimerIntervalRef.current) {
            window.clearInterval(sleepTimerIntervalRef.current);
            sleepTimerIntervalRef.current = null;
          }
          sleepTimerRef.current = null;
          stopInternal();
          setState((prev) => ({ ...prev, sleepTimer: null }));
        } else {
          setState((prev) =>
            prev.sleepTimer?.type === "minutes"
              ? {
                  ...prev,
                  sleepTimer: {
                    ...prev.sleepTimer,
                    remainingSeconds: remaining,
                  },
                }
              : prev
          );
        }
      }, 1000);
    },
    [stopInternal]
  );

  const speakAt = useCallback(
    (index, { retry = 0, primed = false } = {}) => {
      const session = sessionRef.current;
      const item = itemsRef.current[index];
      if (!item) {
        stopInternal({ completed: true });
        return;
      }
      indexRef.current = index;
      setStatus("playing");

      // Chrome/Safari often "complete" the next utterance instantly unless
      // the engine is flushed and given a short gap after the previous one.
      if (!primed && (index > 0 || retry > 0)) {
        cancelSpeech();
        window.setTimeout(() => {
          if (session !== sessionRef.current) return;
          if (statusRef.current !== "playing") return;
          speakAt(index, { retry, primed: true });
        }, SPEAK_GAP_MS);
        return;
      }

      const lang = item.lang;
      const utterance = createUtterance(item.text, lang, voicesRef.current, {
        rate: optionsRef.current.rate,
        voiceURI: optionsRef.current.voiceURI[langPrefix(lang)],
        attachVoice: retry === 0,
      });
      utteranceRef.current = utterance;
      let started = false;
      let settled = false;
      const startedAt = Date.now();

      const finish = () => {
        if (settled || session !== sessionRef.current) return;
        settled = true;
        if (statusRef.current !== "playing") return;
        const ghost = isGhostSpeechEnd({
          started,
          elapsedMs: Date.now() - startedAt,
        });
        if (ghost && retry < 2) {
          speakAt(index, { retry: retry + 1 });
          return;
        }
        speakAt(index + 1);
      };

      utterance.onstart = () => {
        if (session !== sessionRef.current) return;
        started = true;
        setStatus("playing");
        syncMediaSession("playing");
      };
      utterance.onerror = (event) => {
        if (session !== sessionRef.current) return;
        if (isIgnorableSpeechError(event?.error)) return;
        finish();
      };
      utterance.onend = finish;
      speakNow(utterance);
    },
    [setStatus, stopInternal, syncMediaSession]
  );

  const startKeepAlive = useCallback(() => {
    clearKeepAlive();
    if (isLikelyIOS() || !isSpeechSupported()) return;
    keepAliveRef.current = window.setInterval(() => {
      if (statusRef.current !== "playing") return;
      const synth = window.speechSynthesis;
      if (synth.speaking || synth.pending) {
        try {
          synth.resume();
        } catch (error) {
          // ignore
        }
        return;
      }
      speakAt(Math.max(indexRef.current, 0));
    }, KEEP_ALIVE_MS);
  }, [speakAt]);

  const play = useCallback(
    (items, options = {}) => {
      if (!isSpeechSupported()) {
        setStatus("unsupported", { supported: false });
        return;
      }
      const queue = (items || []).filter((item) => item?.text?.trim());
      if (!queue.length) return;
      bumpSession();
      cancelSpeech();
      itemsRef.current = queue;
      indexRef.current = 0;
      if (options.title) {
        optionsRef.current.title = options.title;
      }
      if (typeof navigator !== "undefined" && navigator.mediaSession) {
        try {
          navigator.mediaSession.metadata = new window.MediaMetadata({
            title: optionsRef.current.title || queue[0]?.text?.slice(0, 40) || "Bible",
            artist: options.artist || "",
            album: "",
          });
        } catch (error) {
          // MediaMetadata is optional
        }
      }
      voicesRef.current = window.speechSynthesis.getVoices();
      startKeepAlive();
      if (isLikelyIOS()) {
        speakAt(0);
      } else {
        window.setTimeout(() => {
          speakAt(0);
        }, 40);
      }
    },
    [setStatus, speakAt, startKeepAlive]
  );

  const pause = useCallback(() => {
    if (statusRef.current !== "playing") return;
    if (isLikelyIOS()) {
      bumpSession();
      cancelSpeech();
      setStatus("paused");
      syncMediaSession("paused");
      return;
    }
    const ok = pauseSpeech();
    if (!ok) {
      bumpSession();
      cancelSpeech();
    }
    setStatus("paused");
    syncMediaSession("paused");
  }, [setStatus, syncMediaSession]);

  const resume = useCallback(() => {
    if (statusRef.current !== "paused") return;
    if (isLikelyIOS() || !window.speechSynthesis.paused) {
      speakAt(Math.max(indexRef.current, 0), { primed: true });
      return;
    }
    resumeSpeech();
    setStatus("playing");
    syncMediaSession("playing");
  }, [setStatus, speakAt, syncMediaSession]);

  const stop = useCallback(() => {
    stopInternal({ completed: false });
  }, [stopInternal]);

  const skip = useCallback(
    (delta) => {
      if (statusRef.current !== "playing" && statusRef.current !== "paused") {
        return;
      }
      const next = indexRef.current + delta;
      if (next < 0 || next >= itemsRef.current.length) return;
      const wasPaused = statusRef.current === "paused";
      bumpSession();
      cancelSpeech();
      indexRef.current = next;
      if (wasPaused) {
        setStatus("paused");
        return;
      }
      const session = sessionRef.current;
      window.setTimeout(() => {
        if (session !== sessionRef.current) return;
        speakAt(next, { primed: true });
      }, SPEAK_GAP_MS);
    },
    [setStatus, speakAt]
  );

  const persistOptions = useCallback((partial) => {
    const next = saveSpeechPrefs(partial);
    optionsRef.current = {
      ...optionsRef.current,
      rate: next.rate,
      voiceURI: next.voiceURI,
    };
    setState((prev) => ({
      ...prev,
      rate: next.rate,
      autoAdvance: next.autoAdvance,
      voiceURI: next.voiceURI,
    }));
    return next;
  }, []);

  const restartCurrentIfPlaying = useCallback(() => {
    if (statusRef.current !== "playing") return;
    bumpSession();
    cancelSpeech();
    const session = sessionRef.current;
    const index = Math.max(indexRef.current, 0);
    window.setTimeout(() => {
      if (session !== sessionRef.current) return;
      if (statusRef.current !== "playing") return;
      speakAt(index, { primed: true });
    }, SPEAK_GAP_MS);
  }, [speakAt]);

  const setRate = useCallback(
    (rate) => {
      persistOptions({ rate });
      restartCurrentIfPlaying();
    },
    [persistOptions, restartCurrentIfPlaying]
  );

  const setAutoAdvance = useCallback(
    (autoAdvance) => {
      persistOptions({ autoAdvance });
    },
    [persistOptions]
  );

  const setVoiceURI = useCallback(
    (lang, voiceURI) => {
      const prefix = langPrefix(lang);
      persistOptions({
        voiceURI: {
          ...optionsRef.current.voiceURI,
          [prefix]: voiceURI || "",
        },
      });
      restartCurrentIfPlaying();
    },
    [persistOptions, restartCurrentIfPlaying]
  );

  pauseRef.current = pause;
  resumeRef.current = resume;
  stopRef.current = stop;
  skipRef.current = skip;
  speakAtRef.current = speakAt;

  useEffect(() => {
    if (!isSpeechSupported()) {
      setState((prev) => ({ ...prev, supported: false, status: "unsupported" }));
      return undefined;
    }

    const syncVoices = (voices) => {
      voicesRef.current = voices;
      setState((prev) => ({ ...prev, voices }));
    };

    loadVoices().then(syncVoices);
    const onVoices = () => syncVoices(window.speechSynthesis.getVoices());
    window.speechSynthesis.addEventListener("voiceschanged", onVoices);

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (statusRef.current !== "playing") return;
      const synth = window.speechSynthesis;
      if (synth.paused) {
        resumeSpeech();
        return;
      }
      if (!synth.speaking && !synth.pending) {
        speakAtRef.current(Math.max(indexRef.current, 0));
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onPageHide = () => {
      if (statusRef.current === "idle") return;
      bumpSession();
      cancelSpeech();
    };
    window.addEventListener("pagehide", onPageHide);

    if (navigator.mediaSession) {
      try {
        navigator.mediaSession.setActionHandler("play", () => resumeRef.current());
        navigator.mediaSession.setActionHandler("pause", () => pauseRef.current());
        navigator.mediaSession.setActionHandler("stop", () => stopRef.current());
        navigator.mediaSession.setActionHandler("previoustrack", () => skipRef.current(-1));
        navigator.mediaSession.setActionHandler("nexttrack", () => skipRef.current(1));
      } catch (error) {
        // some browsers reject handlers
      }
    }

    return () => {
      clearKeepAlive();
      window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      bumpSession();
      cancelSpeech();
    };
  }, []);

  return {
    ...state,
    play,
    pause,
    resume,
    stop,
    skip,
    setRate,
    setAutoAdvance,
    setVoiceURI,
    setSleepTimer,
  };
};

export default useSpeech;
